import type { LeadCallingWindow } from "@/lib/types";

/**
 * A lead's preferred calling window (ADR-156 in platform): when they like to be called, on their
 * own clock, inside 8 am to 9 pm. Anybody who may open the lead sets, changes or clears it on the
 * record; a callback sets none (ACL-661: a callback carries its own window). A cadence phase that
 * asks for it starts its rounds inside it; it is a preference, not a limit.
 */

export const EARLIEST = "08:00";
export const LATEST = "21:00";

/** Why core-api would refuse the window, or null: the same rule, said before Save. */
export function leadWindowProblem(start: string, end: string): string | null {
  if (!/^\d\d:\d\d$/.test(start) || !/^\d\d:\d\d$/.test(end)) return "Pick a start and an end";
  if (start < EARLIEST || end > LATEST) return "Inside 8 am to 9 pm on their clock";
  if (start >= end) return "The window ends after it starts";
  return null;
}

/** "16:00" as "4:00 pm". */
export function timeWords(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number) as [number, number];
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
}

/** "4:00 pm to 5:00 pm". */
export function windowWords(w: Pick<LeadCallingWindow, "start" | "end">): string {
  return `${timeWords(w.start)} to ${timeWords(w.end)}`;
}

/** Who set it, for the line under it: "set by Ana", "set by hand". A person's, whatever an older
 *  core-api says its source was. */
export function windowOrigin(w: Pick<LeadCallingWindow, "set_by_name"> & { source?: string }): string {
  const by = w.set_by_name ? ` by ${w.set_by_name}` : "";
  return `set${by || " by hand"}`;
}
